// Shared with mju-ai-workbench/shared/schemas/memorySync.ts — keep the two identical. AIOE owns it.
/**
 * Delta memory sync (25 Oct 2026): a repository's memory goes to its home as what changed, not as the whole graph.
 * Until now a workbench sent every item (`full: true`) whenever graph.json's bytes moved, about a megabyte at a time
 * and dozens of times an hour for an active repository.
 *
 * The workbench keeps, per (home, project, rootId), the digest of every item the home acknowledged. On a change it
 * sends only new and changed items (`upserts`, each with its digest in `digests`) and gone ids (`removes`), with
 * `full: false`. A full push happens only on the first push, when the home's `epoch` changed, or after a manifest
 * check that failed. Reconciliation is by manifest, at most daily:
 *
 *   POST /projects/v1/:id/memory/manifest   { rootId, digest }          -> { same: true, epoch } | { same: false, epoch }
 *   POST /projects/v1/:id/memory/manifest   { rootId, digest, items }   -> { same, epoch, need, gone }
 *
 * then exactly `need` goes up as upserts and `gone` as removes. `epoch` changes when the home lost or reset the
 * root's items (a restore, a clear, a relabel by new labelling rules): a client holding another epoch drops its
 * digest map and pushes full once.
 *
 * Reading, progressively: a workbench keeps only the items it has used (answers, explanations, what a person opened),
 * never the organisation's whole memory, and keeps them current from the changes feed, which names ids and digests
 * only; it fetches again just the ids it holds that changed.
 *
 *   GET  /projects/v1/:id/memory/changes?since=<seq>&limit=   -> MemoryChanges (what this reader may read)
 *   POST /projects/v1/:id/memory/items/batch   { ids }         -> { seq, items, itemsFiltered }
 *
 * Digests: an item's digest is the lowercase hex SHA-256 of the canonical JSON (keys sorted, no whitespace) of the item
 * as the client sends it, after its own masking: `{ id, kind, rootId, scope, data, label }`, with `label` holding only
 * what the client sent. The home stores the digest the client declares and answers the manifest with it, so both sides
 * compare the same bytes. The manifest's `digest` is the SHA-256 of the lines `<id>:<d>\n`, sorted by id.
 */
import { z } from "zod";
import { memoryDeltaSchema } from "./projectMemory.js";
export const MEMORY_SYNC_VERSION = 1;
/** Items a manifest may list in one call; a larger root is reconciled by a full push instead. */
export const MEMORY_MANIFEST_MAX_ITEMS = 50_000;
/** Ids one batch read takes. */
export const MEMORY_BATCH_MAX_IDS = 500;
const digest = z.string().regex(/^[a-f0-9]{64}$/, "a lowercase hex SHA-256");
const id = z.string().min(1).max(300);
const rootId = z.string().max(200).regex(/^[A-Za-z0-9_.:-]*$/, "letters, digits and _ . : -");
/** Changes whenever the home lost or reset a root's items; opaque to the client. */
const epoch = z.string().min(1).max(100);
/** `POST …/memory/deltas` with digests: each upsert's digest, by id. An upsert without one is stored without one. */
export const memorySyncDeltaSchema = memoryDeltaSchema.extend({
    digests: z.record(id, digest).optional(),
});
/** What `…/memory/deltas` answers beyond `{ accepted, removed, seq }`: the root's epoch, and how many upserts were unchanged. */
export const memorySyncDeltaResultSchema = z.object({
    epoch: epoch.optional(),
    unchanged: z.number().int().nonnegative().optional(),
});
export const memoryManifestRequestSchema = z.object({
    rootId,
    digest,
    items: z.array(z.object({ id, d: digest })).max(MEMORY_MANIFEST_MAX_ITEMS).optional(),
});
export const memoryManifestResponseSchema = z.object({
    same: z.boolean(),
    epoch,
    /** With `items`: ids the home lacks or holds with another digest (send them as upserts). */
    need: z.array(id).optional(),
    /** With `items`: ids the home holds for this root that the client does not (send them as removes). */
    gone: z.array(id).optional(),
});
/** `GET …/memory/changes?since=`: ids only, of what this reader may read. `more` says to ask again from `seq`. */
export const memoryChangesSchema = z.object({
    seq: z.number().int().nonnegative(),
    changed: z.array(z.object({ id, kind: z.string(), rootId, d: digest.optional() })),
    removed: z.array(id),
    more: z.boolean().optional(),
    /** The history before `since` is gone (kept days passed): refetch what you hold. */
    reset: z.boolean().optional(),
});
export const memoryBatchReadSchema = z.object({ ids: z.array(id).min(1).max(MEMORY_BATCH_MAX_IDS) });
//# sourceMappingURL=memorySync.js.map